import {
  CREATE_LIVE_ELEMENT_REQUEST, CREATING_LIVE_ELEMENT_SUCCEEDED, CREATING_LIVE_ELEMENT_FAILED,
  FETCHING_LIVE_ELEMENTS_REQUEST, FETCHING_LIVE_ELEMENTS_SUCCEEDED, FETCHING_LIVE_ELEMENTS_FAILED,
  FETCHING_LIVE_ELEMENT_REQUEST, FETCHING_LIVE_ELEMENT_SUCCEEDED, FETCHING_LIVE_ELEMENT_FAILED, INVALIDATE_LIVE_ELEMENT, BREAK_LIVE_ELEMENT_REQUEST,
  DELETE_LIVE_ELEMENT_REQUEST, DELETING_LIVE_ELEMENT_SUCCEEDED, DELETING_LIVE_ELEMENT_FAILED,
  UPDATE_LIVE_ELEMENT_REQUEST, UPDATING_LIVE_ELEMENT_SUCCEEDED, UPDATING_LIVE_ELEMENT_FAILED,
  PUBLISH_LIVE_ELEMENT_REQUEST, PUBLISHING_LIVE_ELEMENT_SUCCEEDED, PUBLISHING_LIVE_ELEMENT_FAILED,
  RENAME_LIVE_ELEMENT_REQUEST,
  CLONE_LIVE_ELEMENT_REQUEST,
  CLONING_LIVE_ELEMENT_SUCCEEDED,
  CLONING_LIVE_ELEMENT_FAILED,
  RELOAD_LIVE_ELEMENT_ON_CHANGE
} from "constants/actionTypes";

import axios from "axios";
import buildUrl from 'build-url';
import _ from 'lodash';
import { createAction } from 'redux-actions';
import { call, put, takeEvery, select } from "redux-saga/effects";

import shortid from 'shortid';
import { Frame } from "scenejs";

import { actionGroup, createWidget, deleteWidgets, updateWidgetLayout } from "./editor";

import { LIVE_ELEMENT_CONTAINER, LIVE_ELEMENT, MENU, MENU_CONTAINER, TEXT, SECTION, SECTION_COLUMN, PAGE, MODAL, SLIDE, CAROUSEL, MENU_DRAGGABLE_CONTAINER } from "constants/widgetTypes";
import { calculateOrderForWidgets, generateWidgetsFromPaletteRequest, getParentContainerOfType, getWidgetType } from "components/Editor/helper/widget";
import { WidgetRegistry } from "components/Editor/widgets/registry";
import { getBoundingRectFromLayout } from "components/Editor/helper/layoutManager";
import { calculateRequestForBreakpoint } from "components/Editor/helper/liveElement";
import { broadcastMessage } from "components/Broadcaster";

const apiUrl = process.env.REACT_APP_CLEVERSTORY_API_URL;
const LIVE_ELEMENT_URL = `${apiUrl}/api/2.0/live_elements`

/**
 * Create live element
*/
export const createLiveElement = createAction(CREATE_LIVE_ELEMENT_REQUEST, (editorSession, request) => {
  return {
    editorSession: editorSession,
    request: request
  };
})

const creatingLiveElementSucceeded = createAction(CREATING_LIVE_ELEMENT_SUCCEEDED, (response) => {
  return response;
})

const creatingLiveElementFailed = createAction(CREATING_LIVE_ELEMENT_FAILED, (error) => {
  return error;
})

function* prepareLiveElementRequest(editorSession, request) {

  const _cleanData = (widget) => {
    const children = widget.widgets;
    if (_.size(children) > 0) {
      widget.widgets = children.map(child => _cleanData(child));
    }

    return _.omit(widget, ['id', 'uid', 'parent', 'template']);
  }

  const clonedWidgets = yield select(state => {
    const widgets = _.get(state, `${editorSession}.present.widgets`);

    const _findWidget = (id) => {
      return widgets.find(widget => widget.uid === id);
    }

    const _findChildren = (parent) => {
      return widgets.filter(widget => widget.parent === parent);
    }

    const _buildCopyRequest = (widget) => {
      let widgetRequest = _.cloneDeep(widget);
      var children = _findChildren(widget.uid);
      const parent = getWidgetType(editorSession, widgetRequest?.parent);
      const includeActions = ['link', 'pageAnchor'];

      if (_.size(children) > 0) {
        widgetRequest.widgets = children
          .map(child => _buildCopyRequest(child))
          .sort((w1, w2) => {
            const o1 = _.get(w1, 'order') || 1;
            const o2 = _.get(w2, 'order') || 1;

            return o1 - o2;
          });
      }

      const processWidgetActions = (widget) => {
        if (widget.type === TEXT && widget.actions && !_.isEmpty(widget.actions) && !includeActions.includes(widget.actions[0]?.type)) {
          widget.content = widget.content.replace(/<a[^>]*>(.*?)<\/a>/g, '$1');
        }
        if (widget.actions && !_.isEmpty(widget.actions) && !includeActions.includes(widget.actions[0]?.type)) {
          widget.actions = [];
        }
      };
    
      const processMenuContainer = (menuWidget) => {
        if (menuWidget.action && !includeActions.includes(menuWidget.action?.type)) {
          menuWidget.action = null;
        }
        menuWidget.children?.forEach((childWidget) => {
          if (!includeActions.includes(childWidget.action?.type)) {
            childWidget.action = null;
          }
        });
      };

      if (widgetRequest.type === MENU || widgetRequest.type === SECTION) {
        const children = widgetRequest?.widgets;
        children?.forEach((widget) => {
          processWidgetActions(widget);
          if (widget.type === MENU_CONTAINER || widget.type === MENU_DRAGGABLE_CONTAINER) {
            widget.content?.forEach((menuWidget) => processMenuContainer(menuWidget));
          }

          if(widget.type === SECTION_COLUMN) {
            widget.widgets?.forEach((columnWidget) => processWidgetActions(columnWidget));
          }
        });
      }

      widgetRequest = {
        ...widgetRequest,
        actions: (widgetRequest.actions || []).map(action => {
          if ((action.type === 'asset' || action.type === 'page' || action.type === 'gating' || action.type === 'slide' || action.type === 'carousel') && parent !== MENU && parent !== SECTION_COLUMN) {
            return null;
          }
          return action;
        }).filter(Boolean)
      }

      return widgetRequest;
    }

    var processedWidgets = request.widgets
      .map(widget => _buildCopyRequest(widget))
      .filter(widget => widget != null)
      .map(widget => {
        var cloned = _.cloneDeep(widget);
        cloned = _cleanData(cloned);

        return cloned;
      });

    return processedWidgets;
  });

  const processedWidgets = generateWidgetsFromPaletteRequest(editorSession, clonedWidgets, request.container.uid);

  var widgets = [request.container, ...processedWidgets];
  widgets = calculateOrderForWidgets(widgets, null);
  widgets = WidgetRegistry.processRequest(widgets);

  return widgets;
}

function* createLiveElementSaga(action) {
  const { editorSession, request } = action.payload;

  const createLiveElementAPI = (request) => {
    return axios.post(LIVE_ELEMENT_URL, request).then(response => response.data);
  }

  try {
    const processedWidgets = yield call(prepareLiveElementRequest, editorSession, request);
    const themes = yield select(state => _.get(state, `${editorSession}.present.themes`));

    const response = yield call(createLiveElementAPI, {
      ...request,
      widgets: processedWidgets,
      themes: themes
    });

    if (response) {
      yield put(creatingLiveElementSucceeded(response));
      var liveElementWidgets = _.get(response, 'content.widgets');

      var uid = `widget_${shortid.generate()}`;

      var liveElementWidgetEntity = {
        uid: uid,
        type: LIVE_ELEMENT,
        name: request.name,
        content: {
          id: response.id,
          createdBy: response.createdBy,
        },
        parent: request.parent,
        properties: {
          desktop: {
            visible: true,
            layout: {
              transform: {
                translate: `${request.layout.desktop.x}px, ${request.layout.desktop.y}px`
              },
              width: request.layout.desktop.width,
              height: request.layout.desktop.height
            }
          },
          mobile: {
            visible: true,
            layout: {
              transform: {
                translate: `${request.layout.mobile.x}px, ${request.layout.mobile.y}px`
              },
              width: request.layout.mobile.width,
              height: request.layout.mobile.height
            }
          }
        },
        children: liveElementWidgets,
        themes: response.themes,
      }

      actionGroup.start();

      yield put(createWidget(editorSession, [liveElementWidgetEntity], liveElementWidgetEntity.parent));

      const widgets = request.widgets.map(widget => widget.uid);
      yield put(deleteWidgets(editorSession, widgets));

      actionGroup.end();
    }
  } catch (error) {
    console.log(error);
    yield put(creatingLiveElementFailed(error))
  }
}

export const invalidateLiveElement = createAction(INVALIDATE_LIVE_ELEMENT);

/**
 * Fetch live elements for workspace
*/
export const fetchLiveElements = createAction(FETCHING_LIVE_ELEMENTS_REQUEST, (request, page, limit = 20) => {
  return {
    request: request,
    page: page,
    limit: limit
  };
});

const fetchingLiveElementsSucceeded = createAction(FETCHING_LIVE_ELEMENTS_SUCCEEDED, (response) => {
  return response
});

const fetchingLiveElementsFailed = createAction(FETCHING_LIVE_ELEMENTS_FAILED, (error) => {
  return error;
});

function* fetchLiveElementsSaga(action) {
  const { request, page, limit } = action.payload;

  let url = buildUrl(LIVE_ELEMENT_URL, {
    path: `search?page=${page}&limit=${limit}`
  });


  const fetchLiveElementsAPI = () => {
    return axios.post(url, request).then(response => response.data);
  }

  try {
    const response = yield call(fetchLiveElementsAPI)
    yield put(fetchingLiveElementsSucceeded(response))
  } catch (error) {
    yield put(fetchingLiveElementsFailed(error))
  }

}

/**
 * Fetch live element for {id}
*/
export const fetchLiveElement = createAction(FETCHING_LIVE_ELEMENT_REQUEST, id => {
  return id;
})

const fetchLiveElementSucceeded = createAction(FETCHING_LIVE_ELEMENT_SUCCEEDED, response => {
  return response;
})

const fetchingLiveElementFailed = createAction(FETCHING_LIVE_ELEMENT_FAILED, (id, error) => {
  return {
    id: id,
    error: error
  }
})

function* fetchingLiveElementSaga(action) {
  const id = action.payload;

  let url = buildUrl(LIVE_ELEMENT_URL, {
    path: id
  });

  const fetchingLiveElementAPI = () => {
    return axios.get(url).then(response => response.data);
  }

  try {
    const response = yield call(fetchingLiveElementAPI);
    yield put(fetchLiveElementSucceeded(response));
  } catch (error) {
    yield put(fetchingLiveElementFailed(id, error));
  }
}


/**
 * Break live element
*/
export const breakLiveElement = createAction(BREAK_LIVE_ELEMENT_REQUEST, (editorSession, liveElement) => {
  return {
    editorSession: editorSession,
    liveElement: liveElement
  };
});

function* breakLiveElementSaga(action) {
  const { editorSession, liveElement } = action.payload;
  const parent = liveElement.parent;
  var widgets = (liveElement.children || []).filter(widget => {
    return widget.type !== LIVE_ELEMENT_CONTAINER
  });
  const storyboard = yield select(state => _.get(state, "storyboards.storyboard.item", null));
  const storyboardType = _.get(storyboard, 'type', '');
  const presentWidgets = yield select(state => _.get(state, `${editorSession}.present.widgets`) || []);

  const _cleanData = (widget) => {
    const children = widget.widgets;
    if (_.size(children) > 0) {
      widget.widgets = children.map(child => _cleanData(child));
    }

    return _.omit(widget, ['id', 'uid', 'parent']);
  }

  const _findWidget = (id) => {
    return presentWidgets.find(widget => widget.uid === id);
  }

  const reCalculateWidgetLayout = (widget, layout, breakpoint) => {
    try {
      var wRect = getBoundingRectFromLayout(layout);

      var parent = _findWidget(widget.parent);
      var pLayout = _.get(parent, `properties.${breakpoint}.layout`)
      var pRect = getBoundingRectFromLayout(pLayout);

      const frame = new Frame(layout);
      var x = wRect.x + pRect.x;
      var y = wRect.y + pRect.y;

      const translate = `${x}px, ${y}px`;

      frame.set("transform", "translate", translate);
      var updatedLayout = frame.get();

      if (parent && !_.includes([PAGE, MODAL, SLIDE, LIVE_ELEMENT_CONTAINER], parent.type)) {
        return reCalculateWidgetLayout(parent, updatedLayout, breakpoint);
      }

      return updatedLayout;
    } catch (error) {
      return 0;
    }
  }

  const calculateRequestForBreakpoint = (editorSession, widgets, breakpoint) => {
    const liveElementLayout = _.get(liveElement, `properties.${breakpoint}.layout`);
    const liveElementRect = getBoundingRectFromLayout(liveElementLayout);
    const liveElementContainer = liveElement.children.find(item => item.type === LIVE_ELEMENT_CONTAINER);

    const updatedWidgets = widgets.map(item => {
      if(item.parent !== liveElementContainer.uid){
        return item;
      }

      const layout = _.get(item, `properties.${breakpoint}.layout`);
      const rect = getBoundingRectFromLayout(layout);

      const frame = new Frame(layout);

      if(frame.has('transform', 'translate')) {
        const calculatedLiveLayout = reCalculateWidgetLayout(liveElement, liveElementLayout, breakpoint);
        const liveY = parseInt(_.get(calculatedLiveLayout, 'transform.translate', '0,0').split(',')[1], 10) || 0;

        let widgetType = _.get(item, 'type');
        let newX = liveElementRect.x + rect.x;
        let newY = liveElementRect.y + rect.y;

        if(widgetType === MENU || widgetType === SECTION || widgetType === CAROUSEL) {
          newY = liveY;
        }

        if(widgetType === MENU && _.get(item, `properties.desktop.stretched`, false)) {
          newX = 0;
          frame.remove('width');
        }

        if(widgetType === SECTION && (storyboardType === 'SPATIAL' || storyboardType === 'INTERACTIVE_CONTENT')) {
          if(breakpoint === 'desktop') {
            item.properties.desktop.stretched = false;
            frame.set("width", '1200px');
          } else {
            frame.set("width", '100%');
          }
          frame.set('transform', 'translate', `${0}px, ${newY}px`);       
          newX = 0;
          if(item?.properties?.desktop) item.properties.desktop.margin = 0;
        }

        frame.set('transform', 'translate', `${newX}px, ${newY}px`);        
      }

      return {
        ...item,
        properties: {
          ...item.properties,
          [breakpoint]: {
            ...item.properties[breakpoint],
            layout: frame.get()
          }
        }
      }
    });

    return updatedWidgets;
  }

  let clonedWidgets = yield select(state => {
    const _findChildren = (parent) => {
      return widgets.filter(widget => widget.parent === parent);
    }

    const _buildCopyRequest = (widget) => {
      const widgetRequest = _.cloneDeep(widget);
      var children = _findChildren(widget.uid);

      if (_.size(children) > 0) {
        widgetRequest.widgets = children
          .map(child => _buildCopyRequest(child))
          .sort((w1, w2) => {
            const o1 = _.get(w1, 'order') || 1;
            const o2 = _.get(w2, 'order') || 1;

            return o1 - o2;
          });
      }

      return widgetRequest;
    }

    var processedWidgets  = calculateRequestForBreakpoint(editorSession, widgets, 'desktop');
    processedWidgets = calculateRequestForBreakpoint(editorSession, processedWidgets, 'mobile');  

    processedWidgets = processedWidgets
      .filter(widget => {
        const moveableConfig = WidgetRegistry.moveableConfig(editorSession, widget);
        return _.get(moveableConfig, 'cloneable', true);
      })
      .map(widget => _buildCopyRequest(widget))
      .filter(widget => widget != null)
      .filter(widget => widgets.find(item => item.uid === widget.parent) == null) 
      .map(widget => {
        var cloned = _.cloneDeep(widget);
        cloned = _cleanData(cloned);
        return cloned;
      });

    return processedWidgets;
  });

  if (_.size(clonedWidgets) == 0) {
    return;
  }

  const excludedWidgets = [];

  clonedWidgets = clonedWidgets.filter(widget => {
    // Exclude SECTION if it contains a widget of target type
    if (widget.type === SECTION || widget.type === SECTION_COLUMN || widget.type === MENU) {
      excludedWidgets.push(widget);
      return false;
    }

    return true;
  });

  actionGroup.start();

  if (clonedWidgets.length) {
    yield put(createWidget(editorSession, clonedWidgets, parent));
  }

  // Handle MENU widgets with adjusted parent
  if (excludedWidgets.length) {
    const parentWidgetId = document.querySelector('[data-storyboard-type]')?.id || parent;
    yield put(createWidget(editorSession, excludedWidgets, parentWidgetId));
  }

  yield put(deleteWidgets(editorSession, [liveElement.uid]));
  
  actionGroup.end();
}

/**
 * Break live element
*/
export const deleteLiveElement = createAction(DELETE_LIVE_ELEMENT_REQUEST, id => {
  return id
}) 

const deleteLiveElementSucceeded = createAction(DELETING_LIVE_ELEMENT_SUCCEEDED, (id, response) => {
  return {
    id: id,
    response: response
  }
})

const deleteLiveElementFailed = createAction(DELETING_LIVE_ELEMENT_FAILED, (id, error) => {
  return {
    id: id,
    error: error
  }
})

function* deleteLiveElementSaga(action) {
  const id = action.payload;

  let url = buildUrl(LIVE_ELEMENT_URL, {
    path: id
  });

  const deleteLiveElementAPI = () => {
    return axios.delete(url).then(response => response.data);
  }

  try {
    const response = yield call(deleteLiveElementAPI);
    yield put(deleteLiveElementSucceeded(id, response));
  } catch (error) {
    yield put(deleteLiveElementFailed(id, error));
  }
}

/**
 * Update Live Element
*/
export const updateLiveElement = createAction(UPDATE_LIVE_ELEMENT_REQUEST, (id, editorSession, callback) => {
  return {
    id: id,
    editorSession: editorSession,
    callback: callback
  }
});

const updatingLiveElementSucceeded = createAction(UPDATING_LIVE_ELEMENT_SUCCEEDED, (id, response) => {
  return { id, response };
});

const updatingLiveElementFailed = createAction(UPDATING_LIVE_ELEMENT_FAILED, (id, error) => {
  return { id, error };
});

function* updateLiveElementSaga(action) {
  const { id, editorSession } = action.payload;

  let url = buildUrl(LIVE_ELEMENT_URL, {
    path: `${id}`
  });

  const updateLiveElementAPI = (request) => {
    return axios({
      method: 'PUT',
      url: url,
      data: request
    }).then(response => response.data);
  }

  try {

    const snapshot = yield select(state => {

      var liveElement = _.get(state, `liveElements.liveElement[${id}].item`);
      var name = _.get(liveElement, 'name', '');
      var themes = _.get(state, `${editorSession}.present.themes`);
      var breakpoint = _.get(state, `${editorSession}.present.breakpoint`);
      var request = { name, themes };

      if (editorSession) {
        var widgets = _.get(state, `${editorSession}.present.widgets`);
        var liveElementContainer = widgets.find(item => item.type === LIVE_ELEMENT_CONTAINER);
        var childrenWidgets = widgets.filter(item => item.type !== LIVE_ELEMENT_CONTAINER);
        
        const desktopRequest = calculateRequestForBreakpoint(editorSession, childrenWidgets, 'desktop', true, liveElementContainer?.uid);
        childrenWidgets = desktopRequest.widgets;

        const mobileRequest = calculateRequestForBreakpoint(editorSession, childrenWidgets, 'mobile', true, liveElementContainer?.uid);
        childrenWidgets = mobileRequest.widgets;

        liveElementContainer = {
          ...liveElementContainer,
          properties: {
            ...liveElementContainer.properties,
            desktop: {
              ...liveElementContainer.properties.desktop,
              layout: {
                translate: '0px, 0px',
                width: `${desktopRequest.width}px`,
                height: `${desktopRequest.height}px`
              }
            },
            mobile: {
              ...liveElementContainer.properties.mobile,
              layout: {
                translate: '0px, 0px',
                width: `${mobileRequest.width}px`,
                height: `${mobileRequest.height}px`
              }
            }
          }
        }

        widgets = [liveElementContainer, ...childrenWidgets];
        widgets = calculateOrderForWidgets(widgets, null);
        widgets = WidgetRegistry.processRequest(widgets);

        request = {
          ...request,
          widgets,
          themes,
        }
      }

      return request;
    });

    const response = yield call(updateLiveElementAPI, snapshot);
    if (response) {
      yield put(updatingLiveElementSucceeded(id, response));
    }

  } catch (error) {
    console.log(error);
    yield put(updatingLiveElementFailed(id, error));
  }
}

/**
 * Rename Live Element
*/
export const renameLiveElement = createAction(RENAME_LIVE_ELEMENT_REQUEST, (id, name) => {
  return {
    id: id,
    name: name,
  }
});

function* renameLiveElementSaga(action) {
  const { id, name } = action.payload;

  let url = buildUrl(LIVE_ELEMENT_URL, {
    path: `${id}`
  });

  const renameLiveElementAPI = (request) => {
    return axios({
      method: 'PUT',
      url: url,
      data: request
    }).then(response => response.data);
  }

  try {
    const request = {
      name: name,
    }

    const response = yield call(renameLiveElementAPI, request);
    if (response) {
      yield put(updatingLiveElementSucceeded(id, response));
    }

  } catch (error) {
    console.log(error);
    yield put(updatingLiveElementFailed(id, error));
  }
}


/**
 * Publish Live Element
*/
export const publishLiveElement = createAction(PUBLISH_LIVE_ELEMENT_REQUEST, (id, callback) => {
  return {
    id: id,
    callback: callback
  }
})

const publishLiveElementSucceeded = createAction(PUBLISHING_LIVE_ELEMENT_SUCCEEDED, (id, response) => {
  return {
    id: id,
    response: response
  }
})

const publishLiveElementFailed = createAction(PUBLISHING_LIVE_ELEMENT_FAILED, (id, error) => {
  return {
    id: id,
    error: error
  }
})

function* publishLiveElementSaga(action) {
  const { id, callback } = action.payload;

  let url = buildUrl(LIVE_ELEMENT_URL, {
    path: `${id}/publish`
  });

  const publishLiveElementAPI = () => {
    return axios({
      method: 'POST',
      url: url,
    }).then(response => response.data);
  }

  try {
    const response = yield call(publishLiveElementAPI);
    yield put(publishLiveElementSucceeded(id, response));

    if (callback) {
      callback(response);
    }

    broadcastMessage(publishLiveElementSucceeded(id, response));

  } catch (error) {
    yield put(publishLiveElementFailed(id, error));
  }
}

/**
 * Clone Live Element
*/
export const cloneLiveElement = createAction(CLONE_LIVE_ELEMENT_REQUEST, (id) => {
  return {
    id: id
  }
})

const cloningLiveElementSucceeded = createAction(CLONING_LIVE_ELEMENT_SUCCEEDED, (id, response) => {
  return {
    id: id,
    response: response
  }
})

const cloningLiveElementFailed = createAction(CLONING_LIVE_ELEMENT_FAILED, (id, error) => {
  return {
    id: id,
    error: error
  }
})

function* cloneLiveElementSaga(action) {
  const { id } = action.payload;

  let url = buildUrl(LIVE_ELEMENT_URL, {
    path: `${id}/clone`
  });

  const publishLiveElementAPI = () => {
    return axios({
      method: 'POST',
      url: url,
    }).then(response => response.data);
  }

  try {
    const response = yield call(publishLiveElementAPI);
    yield put(cloningLiveElementSucceeded(id, response));
  } catch (error) {
    yield put(cloningLiveElementFailed(id, error));
  }
}


export const reloadLiveElementOnChange = createAction(RELOAD_LIVE_ELEMENT_ON_CHANGE, (element) => {
  return {element};
})

export default function* liveElementsSaga() {
  yield takeEvery(FETCHING_LIVE_ELEMENTS_REQUEST, fetchLiveElementsSaga);
  yield takeEvery(FETCHING_LIVE_ELEMENT_REQUEST, fetchingLiveElementSaga);
  yield takeEvery(CREATE_LIVE_ELEMENT_REQUEST, createLiveElementSaga);
  yield takeEvery(BREAK_LIVE_ELEMENT_REQUEST, breakLiveElementSaga);
  yield takeEvery(DELETE_LIVE_ELEMENT_REQUEST, deleteLiveElementSaga);
  yield takeEvery(UPDATE_LIVE_ELEMENT_REQUEST, updateLiveElementSaga);
  yield takeEvery(RENAME_LIVE_ELEMENT_REQUEST, renameLiveElementSaga);
  yield takeEvery(PUBLISH_LIVE_ELEMENT_REQUEST, publishLiveElementSaga);
  yield takeEvery(CLONE_LIVE_ELEMENT_REQUEST, cloneLiveElementSaga);
}